import React from 'react';
import { motion } from 'framer-motion';
import { HiArrowDown } from 'react-icons/hi';

export default function Hero() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-cream"
    >
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gold-100/40 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold-50/60 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full border border-gold-200/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-gold-200/15" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full pt-32 pb-20 lg:pt-40">
        {/* Mobile portrait — shown above text on small screens */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="lg:hidden mb-10 flex justify-center"
        >
          <div className="relative w-48 h-64 rounded-2xl overflow-hidden shadow-xl">
            <img
              src="/dr-noora-portrait.webp"
              alt="Dr. Noora Ibrahim"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Text content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4 mb-8"
            >
              <span className="gold-line" />
              <span className="text-xs tracking-[0.3em] uppercase text-gold-600 font-medium">
                Premium Aesthetic Medicine
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="heading-xl mb-8"
            >
              Reveal Your
              <br />
              <span className="italic text-gold-500">Natural</span>{' '}
              Beauty
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="body-lg max-w-lg mb-10"
            >
              Experience the art of subtle enhancement. Dr. Noora Ibrahim combines
              medical expertise with an eye for aesthetics to help you look and
              feel your most confident self.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="flex flex-wrap gap-4"
            >
              <a href="#contact" className="btn-primary">
                Book Consultation
              </a>
              <a href="#services" className="btn-outline">
                Our Services
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex gap-12 mt-16 pt-10 border-t border-charcoal-100"
            >
              {[
                { number: '10+', label: 'Years Experience' },
                { number: '5000+', label: 'Happy Clients' },
                { number: '15+', label: 'Treatments' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-serif text-3xl md:text-4xl text-charcoal-900 font-medium">
                    {stat.number}
                  </p>
                  <p className="text-xs tracking-wider uppercase text-charcoal-400 mt-1">
                    {stat.label}
                  </p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Hero image placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="hidden lg:block relative"
          >
            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gradient-to-br from-gold-100 to-gold-200">
              <img
                src="/dr-noora-portrait.webp"
                alt="Dr. Noora Ibrahim"
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Floating accent card */}
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-[220px]">
              <p className="font-serif text-lg text-charcoal-900 mb-1">
                Certified Expert
              </p>
              <p className="text-xs text-charcoal-400 leading-relaxed">
                Board-certified in aesthetic medicine with international training
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.a
        href="#about"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-charcoal-400 hover:text-charcoal-600 transition-colors"
      >
        <span className="text-[10px] tracking-[0.2em] uppercase">Scroll</span>
        <HiArrowDown className="animate-bounce" size={16} />
      </motion.a>
    </section>
  );
}
